import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { id: "services", label: "Sectors" },
  { id: "products", label: "Products" },
  { id: "protocol", label: "Protocol" },
  { id: "pricing", label: "Pricing" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-navy-900/80 border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2.5 group"
          >
            <span className="w-8 h-8 bg-gold-500 rounded-sm grid place-items-center font-display font-black text-navy-900 text-lg transition-transform duration-300 group-hover:rotate-45">
              G
            </span>
            <span className="font-display font-extrabold tracking-[0.18em] text-ice-50 text-sm sm:text-base">
              GOLD LINK
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-ice-200/70 hover:text-gold-400 transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
            <a
              data-testid="nav-link-admin"
              href="/admin"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-ice-200/40 hover:text-gold-400 transition-colors duration-300"
            >
              Staff
            </a>
            <button
              data-testid="nav-cta-quote"
              onClick={() => go("quote")}
              className="ml-2 inline-flex items-center gap-1.5 bg-gold-500 text-navy-900 font-heading font-bold text-xs uppercase tracking-widest px-5 py-2.5 rounded-full hover:bg-gold-400 transition-colors duration-300"
            >
              Get a Quote <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(true)}
            className="md:hidden text-ice-50 p-2"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed inset-y-0 right-0 z-[60] w-full sm:w-80 bg-navy-800 border-l border-white/10 flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-[72px] border-b border-white/5">
              <span className="font-display font-extrabold tracking-[0.18em] text-ice-50">
                GOLD LINK
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="text-ice-50 p-2"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex flex-col px-6 py-8 gap-6">
              {[...LINKS, { id: "quote", label: "Get a Quote" }].map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i }}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-3xl font-bold text-ice-50 hover:text-gold-400 transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
              <a
                data-testid="mobile-nav-link-admin"
                href="/admin"
                className="font-mono text-xs uppercase tracking-[0.22em] text-ice-200/40 mt-4"
              >
                Staff Login
              </a>
            </div>
            <div className="mt-auto px-6 pb-8 font-mono text-[11px] text-ice-200/40 leading-relaxed">
              18 The Parade, West Bromwich B71 3NY
              <br />
              +44 7491 147099
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
